import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Music, Mail, Phone, Facebook, Instagram, Youtube, Zap } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://www.rebelangels.ca';
const EVENT_IMAGE = `${SITE_URL}/api/images/gallery/rebel-angels-live-performance-1.jpg`;

// Parse "Oct 10&11, 2026" / "March 20 & 21, 2026" into ISO start/end for schema.org
const parseEventDates = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const yearMatch = dateStr.match(/(19|20|21)\d{2}/);
  if (!yearMatch) return null;
  const year = yearMatch[0];
  const parts = dateStr
    .replace(year, '')
    .replace(/,/g, ' ')
    .split(/&|-|\band\b/i)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  const first = new Date(`${parts[0]} ${year}`);
  if (isNaN(first.getTime())) return null;
  let sH = 20, sM = 0, eH = 23, eM = 0;
  if (timeStr && !/tbd/i.test(timeStr)) {
    const to24 = (h, ap) => { h = parseInt(h, 10); if (!ap) return h; ap = ap.toLowerCase(); if (ap === 'pm' && h < 12) h += 12; if (ap === 'am' && h === 12) h = 0; return h; };
    const range = timeStr.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*(?:to|-|–)\s*(?:(\d{1,2})(?::(\d{2}))?\s*(am|pm)?|midnight|noon)/i);
    const single = timeStr.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
    if (range) {
      sH = to24(range[1], range[3]); sM = parseInt(range[2] || '0', 10);
      if (/midnight/i.test(timeStr)) { eH = 23; eM = 59; }
      else if (/noon/i.test(timeStr)) { eH = 12; eM = 0; }
      else if (range[4]) { eH = to24(range[4], range[6] || range[3]); eM = parseInt(range[5] || '0', 10); }
    } else if (single) {
      sH = to24(single[1], single[3]); sM = parseInt(single[2] || '0', 10);
      eH = sH + 3; eM = sM;
    }
  }
  first.setHours(sH, sM, 0, 0);
  let last;
  if (parts.length > 1) {
    let endStr = parts[1];
    if (/^\d+$/.test(endStr)) {
      const monthMatch = parts[0].match(/[A-Za-z]+/);
      endStr = `${monthMatch ? monthMatch[0] : ''} ${endStr} ${year}`;
    } else {
      endStr = `${endStr} ${year}`;
    }
    last = new Date(endStr);
    if (isNaN(last.getTime())) last = new Date(first);
  } else {
    last = new Date(first);
  }
  last.setHours(eH, eM, 0, 0);
  if (last < first) last.setDate(last.getDate() + 1);
  return { start: first.toISOString(), end: last.toISOString(), startObj: first, endObj: last };
};

const isPrivateEvent = (show) => /private\s+event/i.test(show.title || '');

const buildEventsJsonLd = (shows) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return shows
    .map((show) => {
      const dates = parseEventDates(show.date, show.time);
      if (!dates || dates.endObj < today) return null;
      const priv = isPrivateEvent(show);
      const link = show.ticket_link || show.ticketLink;
      const event = {
        '@context': 'https://schema.org',
        '@type': 'MusicEvent',
        name: priv ? 'Private Event' : show.title,
        startDate: dates.start,
        endDate: dates.end,
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        location: {
          '@type': 'Place',
          name: priv ? 'Private venue' : (show.location || 'Lethbridge, Alberta'),
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Lethbridge',
            addressRegion: 'Alberta',
            addressCountry: 'CA',
          },
        },
        performer: { '@type': 'MusicGroup', name: 'Rebel Angels', url: SITE_URL },
        organizer: { '@type': 'MusicGroup', name: 'Rebel Angels', url: SITE_URL },
        image: [EVENT_IMAGE],
        url: `${SITE_URL}/#shows`,
      };
      if (!priv && link) {
        event.offers = {
          '@type': 'Offer',
          url: link,
          availability: 'https://schema.org/InStock',
          validFrom: dates.start,
        };
      }
      return event;
    })
    .filter(Boolean);
};

const Home = () => {
  const navigate = useNavigate();
  const [clickCount, setClickCount] = useState(0);
  const [shows, setShows] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [socialLinks, setSocialLinks] = useState({
    facebook_url: '',
    instagram_url: '',
    youtube_url: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    message: ''
  });

  useEffect(() => {
    fetchShows();
    fetchGalleryImages();
    fetchSocialLinks();
    
    // Check if we need to scroll to contact section
    const shouldScrollToContact = sessionStorage.getItem('scrollToContact');
    if (shouldScrollToContact) {
      sessionStorage.removeItem('scrollToContact'); // Clear the flag
      // Wait a bit for the page to fully load
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
          console.log('Scrolled to contact section');
        }
      }, 500); // Increased delay to ensure page is fully load
    }
  }, []);

  const fetchShows = async () => {
    try {
      const response = await axios.get(`${API}/shows`);
      
      // Helper function to parse multi-day dates for sorting
      const parseDateForSorting = (dateStr) => {
        // Handle formats like "March 20 & 21, 2026" or "March 20-21, 2026"
        const firstDatePart = dateStr.split(/[&\-]|and/i)[0].trim();
        
        // If it doesn't have a year, add the year from the end of original string
        if (!firstDatePart.match(/\d{4}/)) {
          const yearMatch = dateStr.match(/\d{4}/);
          if (yearMatch) {
            return `${firstDatePart}, ${yearMatch[0]}`;
          }
        }
        return firstDatePart;
      };
      
      // Sort shows by date (earliest first)
      const sortedShows = response.data.sort((a, b) => {
        const parsedDateA = parseDateForSorting(a.date);
        const parsedDateB = parseDateForSorting(b.date);
        const dateA = new Date(parsedDateA);
        const dateB = new Date(parsedDateB);
        return dateA - dateB;
      });
      setShows(sortedShows);
    } catch (error) {
      console.error('Error fetching shows:', error);
      // Use empty array if API fails
      setShows([]);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setGalleryImages([]);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-media`);
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Error fetching social links:', error);
      // Keep default empty links on error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/booking-inquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        event_date: formData.eventDate,
        message: formData.message
      });
      
      toast.success('Booking inquiry sent! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting booking inquiry:', error);
      toast.error('Failed to send inquiry. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="home-container">
      <Helmet>
        {shows.length > 0 && (
          <script type="application/ld+json" data-seo="events-client">
            {JSON.stringify(buildEventsJsonLd(shows))}
          </script>
        )}
      </Helmet>
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels - Premier 50s Cover Band Lethbridge Southern Alberta" 
              className="brand-logo"
            />
          </div>
          <nav className="nav-menu">
            <a href="#about" className="nav-link">About</a>
            <a href="#shows" className="nav-link">Shows</a>
            <a href="#gallery" className="nav-link">Gallery</a>
            <a
              href="/band-members"
              className="nav-link"
              onClick={(e) => { e.preventDefault(); navigate('/band-members'); }}
              data-testid="nav-meet-the-band"
            >
              Meet the Band
            </a>
            <a href="#contact" className="nav-link">Book Us</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
            alt="Rebel Angels - Lethbridge's Premier 50s Rock and Roll Cover Band for Weddings and Events in Southern Alberta" 
            className="hero-logo"
          />
          <h1 className="hero-subtitle">Lethbridge's Premier 50s Cover Band</h1>
          <p className="hero-tagline">
            <span className="tagline-red">Rock</span>{' '}
            <span className="tagline-white">and</span>{' '}
            <span className="tagline-red">Roll</span>{' '}
            <span className="tagline-white">Never</span>{' '}
            <span className="tagline-red">Dies</span>
          </p>
          <div className="hero-buttons">
            <Button className="cta-button" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
              Book Now
            </Button>
            <Button variant="outline" className="cta-button-outline" onClick={() => document.getElementById('shows').scrollIntoView({ behavior: 'smooth' })}>
              View Shows
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <h2 className="section-title">About the Band</h2>
          <div className="about-content">
            <div className="about-text">
              <p className="about-paragraph">
                We are the Rebel Angels, Lethbridge's premier high-energy 50s cover band serving Southern Alberta. We bring the golden age of rock and roll back to life across the region. From Elvis to Chuck Berry, from Buddy Holly to Jerry Lee Lewis, we deliver authentic vintage performances that transport audiences back to the era when rock and roll was young and rebellious.
              </p>
              <p className="about-paragraph">
                Our passion for the music of the 1950s drives every performance. We don't just play the songs – we live them, breathe them, and share the pure joy and excitement that made this music legendary.
              </p>
            </div>
            <div className="about-highlights">
                <div className="highlight-item">
                  <Music className="highlight-icon" />
                  <h3 className="highlight-title">Authentic Sound</h3>
                  <p className="highlight-text">Vintage instruments and classic arrangements</p>
                </div>
                <div className="highlight-item">
                  <Calendar className="highlight-icon" />
                  <h3 className="highlight-title">Event Ready</h3>
                  <p className="highlight-text">Perfect for weddings, corporate events, parties & festivals</p>
                </div>
                <div className="highlight-item clickable" onClick={() => {
                  navigate('/band-members');
                  window.scrollTo(0, 0);
                }}>
                  <MapPin className="highlight-icon" />
                  <h3 className="highlight-title">Local Legends</h3>
                  <p className="highlight-text">Based in Lethbridge, Alberta</p>
                  <p className="highlight-link">Meet the Band →</p>
                </div>
                <div className="highlight-item">
                  <Zap className="highlight-icon" />
                  <h3 className="highlight-title">Full Production</h3>
                  <p className="highlight-text">Sound, lights & decorations available</p>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* Our Shows */}
      <section id="shows" className="shows-section">
        <div className="section-container">
          <h2 className="section-title">Our Shows</h2>
          <div className="shows-grid">
            {shows.map((show) => {
              // Parse date - extract first date for multi-day shows
              const parseDateString = (dateStr) => {
                // Handle formats like "March 20 & 21, 2026" or "March 20-21, 2026"
                // Extract the first date part before &, -, or "and"
                const firstDatePart = dateStr.split(/[&\-]|and/i)[0].trim();
                
                // If it doesn't have a year, try adding the year from the end of original string
                if (!firstDatePart.match(/\d{4}/)) {
                  const yearMatch = dateStr.match(/\d{4}/);
                  if (yearMatch) {
                    return `${firstDatePart}, ${yearMatch[0]}`;
                  }
                }
                return firstDatePart;
              };
              
              const parsedDateStr = parseDateString(show.date);
              const showDate = new Date(parsedDateStr);
              
              // Get today's date at midnight (start of day) for comparison
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              
              // Only mark as past if the show date is BEFORE today (not including today)
              // This means shows grey out the day AFTER they occur
              const isPastShow = !isNaN(showDate.getTime()) && showDate < today;
              
              const handleShowClick = () => {
                if (show.ticketLink || show.ticket_link) {
                  const link = show.ticketLink || show.ticket_link;
                  window.open(link, '_blank', 'noopener,noreferrer');
                }
              };

              const hasLink = show.ticketLink || show.ticket_link;
              
              return (
                <Card 
                  key={show.id} 
                  className={`show-card ${isPastShow ? 'past-show' : 'upcoming-show'} ${hasLink ? 'clickable-show' : ''}`}
                  onClick={hasLink ? handleShowClick : undefined}
                  style={hasLink ? { cursor: 'pointer' } : undefined}
                >
                  <CardContent className="show-card-content">
                    <div className="show-date">
                      <Calendar className="show-icon" />
                      <span>{show.date}</span>
                    </div>
                    <h3 className="show-title">{show.title}</h3>
                    <div className="show-location">
                      <MapPin className="show-icon" />
                      <span>{show.location}</span>
                    </div>
                    <p className="show-time">{show.time}</p>
                    {show.show_info && (
                      <p className="show-info">{show.show_info}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery-section">
        <div className="section-container">
          <h2 className="section-title">Gallery</h2>
          <div className="gallery-grid">
            {galleryImages.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--retro-gray)', gridColumn: '1 / -1' }}>
                No gallery images yet
              </p>
            ) : (
              galleryImages.map((image, index) => (
                <div key={image.id} className={`gallery-item ${index === 0 ? 'large' : ''}`}>
                  <img 
                    src={image.image_url.startsWith('/api') ? `${BACKEND_URL}${image.image_url}` : image.image_url} 
                    alt={image.alt_text || 'Gallery image'}
                    className="gallery-image"
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <h2 className="section-title">Book the Rebel Angels</h2>
          <div className="contact-subtitle">
            <p>Ready to bring the spirit of the 50s to your event? We're here to make it happen!</p>
            <p>Whether it's a private party, corporate event, wedding, or festival, the Rebel Angels are ready to rock your venue with authentic 50s energy. Get in touch today to check availability and discuss your event needs.</p>
          </div>
          <div className="contact-content">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <Input 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleChange} 
                    required 
                    className="form-input"
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <Input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    required 
                    className="form-input"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <Input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleChange} 
                    className="form-input"
                    placeholder="(403) 555-0123"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Event Date</label>
                  <Input 
                    type="date" 
                    name="eventDate" 
                    value={formData.eventDate} 
                    onChange={handleChange} 
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Message *</label>
                <Textarea 
                  name="message" 
                  value={formData.message} 
                  onChange={handleChange} 
                  required 
                  className="form-textarea"
                  placeholder="Tell us about your event..."
                  rows={5}
                />
              </div>
              <Button type="submit" className="submit-button">
                Send Booking Inquiry
              </Button>
            </form>
            <div className="contact-info">
              <h3 className="contact-info-title">Get in Touch</h3>
              <div className="contact-item">
                <Mail className="contact-icon" />
                <span>bookings@rebelangels.ca</span>
              </div>
              <div className="contact-item">
                <Phone className="contact-icon" />
                <a href="tel:+14033602071" className="contact-link">(403) 360-2071</a>
              </div>
              <div className="contact-item">
                <MapPin className="contact-icon" />
                <span>Lethbridge, Alberta</span>
              </div>
              <div className="social-links">
                {socialLinks.facebook_url && (
                  <a href={socialLinks.facebook_url} className="social-link" target="_blank" rel="noopener noreferrer">
                    <Facebook className="social-icon" />
                  </a>
                )}
                {socialLinks.instagram_url && (
                  <a href={socialLinks.instagram_url} className="social-link" target="_blank" rel="noopener noreferrer">
                    <Instagram className="social-icon" />
                  </a>
                )}
                {socialLinks.youtube_url && (
                  <a href={socialLinks.youtube_url} className="social-link" target="_blank" rel="noopener noreferrer">
                    <Youtube className="social-icon" />
                  </a>
                )}
                {!socialLinks.facebook_url && !socialLinks.instagram_url && !socialLinks.youtube_url && (
                  <span className="text-gray-400 text-sm">Social media links coming soon!</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p>&copy; 2025 Rebel Angels. All rights reserved.</p>
          <p 
            className="footer-tagline" 
            onClick={() => {
              const newCount = clickCount + 1;
              setClickCount(newCount);
              if (newCount === 7) {
                navigate('/admin/login');
                setClickCount(0);
              }
            }}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            Keep the spirit of rock and roll alive
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;